import { DestroyRef, Injectable, signal, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, retry, timer } from 'rxjs';
import type { TransactionColumn, ColumnResponse } from '../models';
import { FALLBACK_COLUMNS } from '../models';
import { NotificationService, ColumnPreferencesService } from '../services';
import { TransactionApi } from '../apis';

/**
 * TransactionColumnStore
 *
 * Owns the transaction table's dynamic column configuration: the column
 * definitions, their visibility, ordering, and the loading of definitions from
 * the server (with retry + fallback). Persistence is delegated to
 * {@link ColumnPreferencesService}.
 *
 * Extracted from `TransactionStore` so column customisation — a concern that is
 * expected to grow — lives in a single, independently testable unit. The
 * `root`-provided singleton preserves the original singleton semantics; the
 * public signals/methods are re-exposed unchanged by `TransactionStore`.
 */
@Injectable({ providedIn: 'root' })
export class TransactionColumnStore {
  private readonly transactionApi = inject(TransactionApi);
  private readonly notificationService = inject(NotificationService);
  private readonly columnPrefs = inject(ColumnPreferencesService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _columns = signal<TransactionColumn[]>([]);
  private readonly _columnsLoading = signal<boolean>(false);
  private readonly _columnsError = signal<string | null>(null);
  private readonly _columnVisibility = signal<Map<string, boolean>>(new Map());
  private readonly _columnWidths = signal<Map<string, number>>(new Map());

  readonly columns = this._columns.asReadonly();
  readonly columnsLoading = this._columnsLoading.asReadonly();
  readonly columnsError = this._columnsError.asReadonly();
  readonly columnVisibility = this._columnVisibility.asReadonly();
  /** Map of column key → user-resized width (px). Empty when using defaults. */
  readonly columnWidths = this._columnWidths.asReadonly();

  /**
   * Visible columns (filtered by visibility state).
   * Returns an empty array until visibility is initialised, to prevent showing
   * all columns before the configuration has loaded.
   */
  readonly visibleColumns = computed<TransactionColumn[]>(() => {
    const cols = this._columns();
    const visibility = this._columnVisibility();
    if (visibility.size === 0) {
      return [];
    }
    return cols.filter((col) => visibility.get(col.key) === true);
  });

  /** Sortable visible columns (used to populate the sort panel). */
  readonly sortableColumns = computed<TransactionColumn[]>(() =>
    this.visibleColumns().filter((col) => col.sortable),
  );

  /**
   * Load dynamic column definitions from the server.
   * Retries twice with exponential backoff, then falls back to
   * {@link FALLBACK_COLUMNS}. Never blocks the UI.
   *
   * @param forceRefresh - Force reload even if columns are already loaded.
   */
  loadColumns(forceRefresh = false): void {
    if (this._columns().length > 0 && !forceRefresh) {
      return;
    }

    this._columnsLoading.set(true);
    this._columnsError.set(null);

    this.transactionApi
      .getColumns()
      .pipe(
        retry({
          count: 2,
          delay: (error, retryCount) => {
            console.warn(`Column fetch attempt ${retryCount} failed, retrying...`, error);
            return timer(300 * retryCount);
          },
        }),
        catchError((error) => {
          console.error('Column fetch failed after retries, using fallback columns:', error);
          this._columnsError.set('Using default columns (server unavailable)');
          return of({ columns: FALLBACK_COLUMNS } as ColumnResponse);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response: ColumnResponse) => {
          this._columnsLoading.set(false);

          // Initialise order + visibility from stored prefs on first load;
          // on a forced refresh with existing visibility, only swap columns.
          if (this._columnVisibility().size === 0) {
            this.applyResolved(response.columns);
          } else {
            this._columns.set(response.columns);
          }

          if (this._columnsError()) {
            this.notificationService.warning(
              'Colonnes par défaut utilisées',
              'Configuration serveur indisponible',
            );
          }
        },
        error: (error) => {
          // Belt-and-suspenders: catchError should handle this
          console.error('Unhandled error in column loading:', error);
          this._columns.set(FALLBACK_COLUMNS);
          this._columnsLoading.set(false);
          this._columnsError.set('Using default columns');

          if (this._columnVisibility().size === 0) {
            this.applyResolved(FALLBACK_COLUMNS);
          }
        },
      });
  }

  /**
   * Toggle a column's visibility. Prevents hiding the last visible column.
   * @returns `true` if toggled, `false` if prevented (last visible column).
   */
  toggleColumnVisibility(columnKey: string): boolean {
    const currentVisibility = this._columnVisibility();
    const currentlyVisible = currentVisibility.get(columnKey) ?? false;

    if (currentlyVisible) {
      const visibleCount = Array.from(currentVisibility.values()).filter(Boolean).length;
      if (visibleCount <= 1) {
        return false;
      }
    }

    const newVisibility = new Map(currentVisibility);
    newVisibility.set(columnKey, !currentlyVisible);
    this._columnVisibility.set(newVisibility);
    this.persist();

    return true;
  }

  /** Make all columns visible. */
  showAllColumns(): void {
    const newVisibility = new Map(this._columnVisibility());
    this._columns().forEach((col) => newVisibility.set(col.key, true));
    this._columnVisibility.set(newVisibility);
    this.persist();
  }

  /** Hide all columns except the first (maintains minimum 1 visible column). */
  hideAllColumns(): void {
    const newVisibility = new Map(this._columnVisibility());
    this._columns().forEach((col, index) => newVisibility.set(col.key, index === 0));
    this._columnVisibility.set(newVisibility);
    this.persist();
  }

  /**
   * Reorder columns after a drag-and-drop within the visible columns array.
   * Hidden columns remain appended at the end. Persists the result.
   *
   * @param fromIdx - Drag origin index within the **visible** columns array
   * @param toIdx   - Drop target index within the **visible** columns array
   */
  reorderVisibleColumn(fromIdx: number, toIdx: number): void {
    if (fromIdx === toIdx) return;

    const visibility = this._columnVisibility();
    const visible = this._columns().filter((c) => visibility.get(c.key) === true);
    const hidden = this._columns().filter((c) => visibility.get(c.key) !== true);

    const reordered = [...visible];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    this._columns.set([...reordered, ...hidden]);
    this.persist();
  }

  /**
   * Lower bound for a persisted column width (px). Guards against restoring an
   * unusably narrow column while the table's own resize floor is not yet
   * shipped — keeps this store correct in isolation.
   */
  private static readonly MIN_COLUMN_WIDTH = 80;

  /**
   * Persist a column's manually-resized width and update local state. Widths are
   * clamped to {@link MIN_COLUMN_WIDTH} and rounded to whole pixels.
   *
   * @param columnKey - Server column key (snake_case), NOT the table cell key
   * @param width     - New width in pixels
   */
  setColumnWidth(columnKey: string, width: number): void {
    const clamped = Math.max(TransactionColumnStore.MIN_COLUMN_WIDTH, Math.round(width));
    const newWidths = new Map(this._columnWidths());
    newWidths.set(columnKey, clamped);
    this._columnWidths.set(newWidths);
    this.persist();
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  /** Resolve stored prefs against API columns and initialise the signals. */
  private applyResolved(apiColumns: TransactionColumn[]): void {
    const { columns, visibility, widths } = this.columnPrefs.resolve(apiColumns);
    this._columns.set(columns);
    this._columnVisibility.set(visibility);
    this._columnWidths.set(widths);
  }

  /** Persist the current column order + visibility + widths. */
  private persist(): void {
    this.columnPrefs.save(this._columns(), this._columnVisibility(), this._columnWidths());
  }
}
